import { useState, useEffect, useCallback, useRef } from 'react';
import { api, ApiError, asList } from '../lib/api';
import { QrScanner, isScannerSupported } from '../components/QrScanner';

/* ============================================================================
   THE GUARD'S SCREEN

   A guard at a gate, with a queue of parents, needs a DECISION — not a form.
   So: one input, and then a big green tick or a big red cross.

   TWO WAYS IN, deliberately:
     • Scan the QR with the camera.
     • TYPE THE 6-DIGIT CODE.

   The typed code is not a fallback afterthought — it is the guarantee. Cameras
   fail, lenses get dirty, screens glare in the sun. A pickup system that stops
   working when a camera does is a pickup system that doesn't work.
   ========================================================================== */

interface ScanResult {
  accepted: boolean; message: string;
  studentName: string | null; admissionNumber: string | null; classLabel: string | null;
  guardianName: string | null; reason: string | null;
}
interface Release {
  id: string; studentName: string; personName: string; claimedRelation: string;
  status: string; refusalReason: string | null; requestedAt: string;
}
interface GateScan {
  id: string; studentName: string | null; guardianName: string | null;
  accepted: boolean; rejectionReason: string | null; gateName: string | null; at: string;
}

export function GatePage() {
  const [passId, setPassId] = useState('');
  const [code, setCode] = useState('');
  const [gateName, setGateName] = useState(() => localStorage.getItem('eduvora.gate') ?? 'Main gate');
  const [result, setResult] = useState<ScanResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [scans, setScans] = useState<GateScan[]>([]);
  const [override, setOverride] = useState(false);
  const [myRequests, setMyRequests] = useState<Release[]>([]);

  const codeRef = useRef<HTMLInputElement | null>(null);

  // Camera scanning is an ACCELERATOR over the typed code, never a replacement — see QrScanner.
  const [scanning, setScanning] = useState(false);
  const canScan = isScannerSupported();

  const loadScans = useCallback(() => {
    api.get<GateScan[]>('/api/gate/scans').then((r) => setScans(asList(r))).catch(() => setScans([]));
    // The guard raised a request — they need to see what the office decided.
    api.get<Release[]>('/api/gate/release/today').then((r) => setMyRequests(asList(r))).catch(() => setMyRequests([]));
  }, []);

  useEffect(() => {
    loadScans();
    const t = setInterval(loadScans, 10_000);
    return () => clearInterval(t);
  }, [loadScans]);

  useEffect(() => { localStorage.setItem('eduvora.gate', gateName); }, [gateName]);

  // Takes the values explicitly so the scanner can submit the instant it decodes, without waiting
  // for a state update to land. The typed form passes its own state in.
  const check = async (pid: string, c: string) => {
    if (!pid.trim() || !c.trim()) return;
    setBusy(true); setError(null); setResult(null);
    try {
      const r = await api.post<ScanResult>('/api/gate/scan', {
        passId: pid.trim(), code: c.trim(), gateName,
      });
      setResult(r);

      // ⚠ A SOUND, because the guard is looking at the CAMERA, not at this panel.
      //
      // They are holding a phone up to a parent's screen with a child waiting. The result appears
      // somewhere they are not looking. A short beep for accepted and a lower double-beep for
      // refused means they can keep their eyes on the queue.
      //
      // Wrapped and ignored on failure: some browsers block audio until the user has interacted,
      // and a gate that throws because it could not beep would be a far worse bug than a silent one.
      try { beep(r.accepted); } catch { /* audio is a courtesy, never a requirement */ }
      setPassId(''); setCode('');
      loadScans();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not check the pass.');
    } finally {
      setBusy(false);
      codeRef.current?.focus();
    }
  };

  const submit = () => check(passId, code);

  const rejectedToday = scans.filter((s) => !s.accepted).length;

  return (
    <div>
      <div className="page-head">
        <div>
          <div className="eyebrow" style={{ marginBottom: 6 }}>Security</div>
          <h1 className="h-display" style={{ fontSize: 28 }}>The gate</h1>
        </div>
      </div>

      {/* A refused scan means SOMEBODY TRIED. Surface it. */}
      {rejectedToday > 0 && (
        <div className="pill pill-warn" style={{ padding: '10px 12px', borderRadius: 8, marginBottom: 16, justifyContent: 'flex-start' }}>
          <strong>{rejectedToday} pass{rejectedToday === 1 ? '' : 'es'} refused today.</strong>
          &nbsp;Check the log below.
        </div>
      )}

      <div className="grid-auto">
        {/* ---- the scanner ---- */}
        <div className="card" style={{ padding: 20 }}>
          <h2 style={{ fontSize: 16, fontFamily: 'var(--font-display)', marginBottom: 4 }}>
            Check a pickup pass
          </h2>
          <p style={{ fontSize: 12.5, color: 'var(--ink-3)', marginBottom: 16 }}>
            {canScan
              ? 'Scan the QR — or type the 6-digit code if the camera struggles.'
              : /* ⚠ No camera at all (a desktop at the office, or camera permission blocked at
                   the OS level). The typed path is full parity, so say so plainly rather than
                   implying something is broken. */
                "Type the 6-digit code from the parent's phone."}
          </p>

          {/* ---- camera scan: the fast path ---- */}
          {canScan && !scanning && (
            <button className="btn btn-accent" style={{ width: '100%', marginBottom: 14, padding: '12px' }}
              onClick={() => setScanning(true)}>
              Scan pass with camera
            </button>
          )}
          {scanning && (
            <QrScanner
              onClose={() => setScanning(false)}
              /* ⚠ CLOSE THE CAMERA ON A SUCCESSFUL DECODE.
                 The scanner deliberately stops decoding after one pass — a pass is redeem-once, so
                 a second read can only ever be wrong. Leaving the camera open would strand the
                 guard staring at a frozen preview while the answer sits behind it.
                 Closing hands them straight to the ✓/✕ panel, and "Scan pass with camera" is
                 right there for the next parent. */
              onScan={(pid, c) => {
                setPassId(pid); setCode(c);
                setScanning(false);
                void check(pid, c);
              }}
            />
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <div>
              <label className="label">Gate</label>
              <input className="input" value={gateName} onChange={(e) => setGateName(e.target.value)} />
            </div>

            <div>
              <label className="label">Pass ID</label>
              <input className="input mono" value={passId} placeholder="From the QR, or ask the parent"
                onChange={(e) => setPassId(e.target.value)} style={{ fontSize: 13 }} />
            </div>

            <div>
              <label className="label">6-digit code</label>
              <input ref={codeRef} className="input mono" value={code}
                placeholder="000000" inputMode="numeric" maxLength={6}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                onKeyDown={(e) => { if (e.key === 'Enter') void submit(); }}
                style={{ fontSize: 24, letterSpacing: '0.18em', textAlign: 'center' }} />
            </div>

            <button className="btn btn-accent" disabled={busy || !passId.trim() || code.length < 6}
              onClick={submit} style={{ width: '100%', justifyContent: 'center', padding: '14px' }}>
              {busy ? 'Checking…' : 'Check pass'}
            </button>
          </div>

          {error && (
            <div className="pill pill-danger" style={{ padding: '9px 12px', borderRadius: 8, marginTop: 12, justifyContent: 'flex-start' }}>
              {error}
            </div>
          )}
        </div>

        {/* ---- the verdict: blunt, on purpose ---- */}
        <div className="card" style={{
          padding: 20,
          borderWidth: result ? 2 : 1,
          borderColor: result ? (result.accepted ? 'var(--ok-ink, #1e7d4f)' : 'var(--brand)') : undefined,
          background: result
            ? (result.accepted
              ? 'color-mix(in srgb, #1e7d4f 8%, var(--surface))'
              : 'color-mix(in srgb, var(--brand) 8%, var(--surface))')
            : undefined,
        }}>
          {!result ? (
            <p style={{ color: 'var(--ink-3)', fontSize: 14, textAlign: 'center', padding: '40px 0' }}>
              The result will appear here.
            </p>
          ) : (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 56, lineHeight: 1, marginBottom: 8 }}>
                {result.accepted ? '✓' : '✕'}
              </div>

              <h3 style={{
                fontSize: 18, fontFamily: 'var(--font-display)', marginBottom: 10,
                color: result.accepted ? 'var(--ok-ink, #1e7d4f)' : 'var(--brand)',
              }}>
                {result.accepted ? 'Verified' : 'REFUSED'}
              </h3>

              {result.studentName && (
                <div style={{ marginBottom: 10 }}>
                  <strong style={{ fontSize: 18 }}>{result.studentName}</strong>
                  <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>
                    {result.admissionNumber}{result.classLabel && ` · ${result.classLabel}`}
                  </div>
                </div>
              )}

              {result.reason && (
                <p style={{ fontSize: 13, color: 'var(--ink-2)', marginBottom: 10 }}>
                  Reason given: {result.reason}
                </p>
              )}

              <p style={{
                fontSize: 14, fontWeight: 600,
                color: result.accepted ? 'var(--ink)' : 'var(--brand)',
              }}>
                {result.message}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* ---- the override: a parent with a dead phone ---- */}
      <div className="card" style={{ padding: 16, marginTop: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <div>
            <strong style={{ fontSize: 14.5 }}>No pass? Phone dead?</strong>
            <p style={{ fontSize: 12.5, color: 'var(--ink-2)', marginTop: 2 }}>
              Raise a request. <strong>The office</strong> will verify them and decide —
              you cannot release the child yourself.
            </p>
          </div>
          <button className="btn btn-accent" style={{ marginLeft: 'auto' }} onClick={() => setOverride(true)}>
            Ask the office
          </button>
        </div>
      </div>

      {/* What the office decided about the guard's own requests. */}
      {myRequests.length > 0 && (
        <div style={{ marginTop: 16 }}>
          {myRequests.map((r) => (
            <div key={r.id} className="card" style={{
              padding: 14, marginBottom: 8,
              borderColor: r.status === 'Approved' ? 'var(--ok-ink, #1e7d4f)'
                : r.status === 'Refused' ? 'var(--brand)' : undefined,
            }}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <strong style={{ fontSize: 14 }}>{r.studentName}</strong>
                <span style={{ fontSize: 13, color: 'var(--ink-2)' }}>
                  — {r.personName} ({r.claimedRelation})
                </span>
                <span className={`pill ${
                  r.status === 'Approved' ? 'pill-ok'
                    : r.status === 'Refused' ? 'pill-danger' : 'pill-warn'
                }`} style={{ fontSize: 11, marginLeft: 'auto' }}>
                  {r.status === 'Requested' ? 'Waiting for the office' : r.status}
                </span>
              </div>

              {r.status === 'Approved' && (
                <div style={{ fontSize: 13, color: 'var(--ok-ink, #1e7d4f)', marginTop: 6, fontWeight: 600 }}>
                  ✓ Verified by the office. You may release the child.
                </div>
              )}
              {r.status === 'Refused' && (
                <div style={{ fontSize: 13, color: 'var(--brand)', marginTop: 6, fontWeight: 600 }}>
                  ✕ Refused — {r.refusalReason}. Do not release the child.
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {override && <OverrideDialog gateName={gateName} onClose={() => setOverride(false)} onDone={() => { setOverride(false); loadScans(); }} />}

      {/* ---- the log: accepted AND rejected ---- */}
      <div style={{ marginTop: 24 }}>
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 8 }}>
          Today at the gate
        </div>

        {scans.length === 0 ? (
          <div className="card" style={{ padding: 20, textAlign: 'center', color: 'var(--ink-3)' }}>
            No scans yet today.
          </div>
        ) : (
          <div className="card" style={{ overflow: 'hidden' }}>
            {scans.map((s) => (
              <div key={s.id} style={{
                display: 'flex', gap: 10, alignItems: 'center',
                padding: '11px 14px', borderBottom: '1px solid var(--line-2)',
                background: s.accepted ? undefined : 'color-mix(in srgb, var(--brand) 5%, transparent)',
              }}>
                <span style={{ fontSize: 15 }}>{s.accepted ? '✓' : '✕'}</span>
                <span style={{ fontSize: 14 }}>{s.studentName ?? 'Unknown pass'}</span>
                {!s.accepted && (
                  <span style={{ fontSize: 12.5, color: 'var(--brand)' }}>
                    — {s.rejectionReason}
                  </span>
                )}
                <span className="mono" style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ink-3)' }}>
                  {new Date(s.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  {s.gateName && ` · ${s.gateName}`}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}


/* -------------------------------------------------------------------------- */

/**
 * The guard raises a request. Note what this dialog CANNOT do: release the child.
 *
 * A guard is often a contractor, at a gate, under pressure from an insistent adult — precisely the
 * person a determined stranger would target ("my phone died, just let her out"). So the guard's job
 * is to WRITE DOWN who is standing there. The office decides.
 */
function OverrideDialog({ gateName, onClose, onDone }: {
  gateName: string; onClose: () => void; onDone: () => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<{ id: string; fullName: string; admissionNumber: string }[]>([]);
  const [picked, setPicked] = useState<{ id: string; fullName: string } | null>(null);
  const [f, setF] = useState({ personName: '', claimedRelation: '', reason: 'Phone dead / no pass' });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); return; }
    const t = setTimeout(() => {
      // ⚠ The GATE lookup — not /api/students.
      //
      // /api/students is behind the "staff" policy and returns the full record: guardian names,
      // guardian MOBILE NUMBERS, class, status. A guard doesn't need the roster; they need to
      // identify a child. This returns three fields and nothing else.
      api.get<{ id: string; fullName: string; admissionNumber: string }[]>(
        `/api/gate/lookup/students?search=${encodeURIComponent(query)}`)
        .then((r) => setResults(asList(r)))
        .catch(() => setResults([]));
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const save = async () => {
    if (!picked || !f.personName.trim() || !f.claimedRelation.trim()) return;
    setBusy(true); setError(null);
    try {
      await api.post('/api/gate/release', {
        studentId: picked.id,
        personName: f.personName.trim(),
        claimedRelation: f.claimedRelation.trim(),
        reason: f.reason.trim(),
        gateName,
      });
      onDone();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Could not raise the request.');
    } finally { setBusy(false); }
  };

  return (
    <div className="sheet-backdrop" onClick={onClose}>
      <div className="sheet" onClick={(e) => e.stopPropagation()}>
        <h2 className="h-display" style={{ fontSize: 19, marginBottom: 4 }}>Ask the office</h2>
        <p style={{ color: 'var(--ink-2)', fontSize: 13, marginBottom: 16 }}>
          Write down who is at the gate. The office will ring the number we have on file and decide.
          <strong> Do not release the child until they say so.</strong>
        </p>

        {error && (
          <div className="pill pill-danger" style={{ padding: '9px 12px', borderRadius: 8, marginBottom: 12, justifyContent: 'flex-start' }}>
            {error}
          </div>
        )}

        <label className="label">Which child?</label>
        <input className="input" value={picked ? picked.fullName : query}
          placeholder="Search by name…"
          onChange={(e) => { setPicked(null); setQuery(e.target.value); }} />

        {!picked && results.length > 0 && (
          <div className="card" style={{ marginTop: 4, padding: 0, overflow: 'hidden' }}>
            {results.map((r) => (
              <button key={r.id} onClick={() => { setPicked(r); setQuery(''); }}
                style={{
                  display: 'flex', gap: 8, width: '100%', padding: '9px 12px', textAlign: 'left',
                  background: 'none', border: 'none', borderBottom: '1px solid var(--line-2)', cursor: 'pointer',
                }}>
                <span>{r.fullName}</span>
                <span className="mono" style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ink-3)' }}>
                  {r.admissionNumber}
                </span>
              </button>
            ))}
          </div>
        )}

        {picked && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
            <div>
              <label className="label">Who is at the gate?</label>
              <input className="input" placeholder="Their name, as they give it"
                value={f.personName} onChange={(e) => setF({ ...f, personName: e.target.value })} />
            </div>
            <div>
              <label className="label">What do they say they are?</label>
              <input className="input" placeholder="Father / mother / uncle"
                value={f.claimedRelation} onChange={(e) => setF({ ...f, claimedRelation: e.target.value })} />
            </div>
            <div>
              <label className="label">Why is there no pass?</label>
              <input className="input" value={f.reason}
                onChange={(e) => setF({ ...f, reason: e.target.value })} />
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 8, marginTop: 18 }}>
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn btn-accent"
            disabled={busy || !picked || !f.personName.trim() || !f.claimedRelation.trim()}
            onClick={save} style={{ marginLeft: 'auto' }}>
            {busy ? 'Sending…' : 'Send to the office'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * A short tone for accepted, a lower double-tone for refused.
 *
 * ⚠ WHY THERE IS NO AUDIO FILE. A .mp3 is a network request that can fail, a file to host, and a
 * cache entry that goes stale. WebAudio synthesises this in a few lines with nothing to load, which
 * matters at a school gate on a patchy connection.
 *
 * ⚠ Created per call and closed after. A long-lived AudioContext left open holds the device audio
 * hardware awake, and on a phone in a guard's pocket all day that is a real battery cost.
 */
function beep(accepted: boolean): void {
  const Ctor = window.AudioContext
    ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!Ctor) return;

  const ctx = new Ctor();

  const tone = (freq: number, startAt: number, ms: number) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = 'sine';
    osc.frequency.value = freq;
    // ⚠ Ramped, not switched. A square-edged gain change produces an audible click that sounds
    // like a fault on a cheap phone speaker.
    gain.gain.setValueAtTime(0.0001, ctx.currentTime + startAt);
    gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + startAt + 0.01);
    gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + startAt + ms / 1000);
    osc.connect(gain).connect(ctx.destination);
    osc.start(ctx.currentTime + startAt);
    osc.stop(ctx.currentTime + startAt + ms / 1000 + 0.02);
  };

  if (accepted) {
    tone(880, 0, 140);
  } else {
    // Lower and doubled — distinguishable without looking, and without sounding alarming to a
    // parent standing right there.
    tone(320, 0, 160);
    tone(320, 0.22, 160);
  }

  window.setTimeout(() => { void ctx.close(); }, 900);
}
